import { DependencyList, useCallback, useMemo, useRef, useState } from 'react';

type StateProducer<TState> = TState | ((prevState: TState) => TState);
function extractStateFromInitiator<TState>(
  initiator: StateProducer<TState>,
  prevState: TState
): TState {
  if (initiator instanceof Function) {
    return initiator(prevState);
  }
  return initiator;
}

/*
This hook is combines reduce, useMemo and useState logic, and it is primarily meant to be used when state is derived props or some other state.
- When deps list is empty list: This hooks works 100% same as normal useState hook
- When deps list changes, state is redefined just like with useMemo, but previous state is passed to it as argument,
during initialization this argument is undefined
*/
export default function useDerivedState<TState>(
  initialState: StateProducer<TState | undefined>,
  deps?: DependencyList
): [TState, (dispatch: StateProducer<TState>) => void] {
  const [state, setState] = useState<{ wrapper?: TState }>({
    wrapper: undefined,
  });
  const stateRef = useRef<TState>();
  useMemo(() => {
    stateRef.current = state.wrapper as TState; // this will actually be undefined on first render
  }, [state]);

  useMemo(() => {
    stateRef.current = extractStateFromInitiator(
      initialState,
      stateRef.current
    );
  }, deps);
  const handleSetState = useCallback(
    (nextState: StateProducer<TState>) => {
      const nextStateValue = extractStateFromInitiator<TState>(
        nextState,
        stateRef.current as TState
      );

      if (nextStateValue === stateRef.current) {
        // No need to update because state is up to date
        return;
      }
      /* Reacts setState does not update state if previous state is same as nextState
       * This is why state is wrapper inside object to force component to update */
      setState({ wrapper: nextStateValue });
    },
    [setState]
  );
  return [stateRef.current as TState, handleSetState];
}
